import cn from "classnames";
import { useRef } from "react";
import {
  Checkmark as CheckmarkIcon,
  Minus as MinusIcon,
} from "@rshbintech.dboul/icons";
import { Text } from "../text";
import { SIZES } from "./constants";
import { CheckboxProps } from "./types";
import styles from "./styles.module.scss";

export const Checkbox = ({
  id,
  name,
  size = SIZES.LARGE,
  label,
  children,
  value,
  checked,
  indeterminate = false,
  disabled,
  onChange,
  className,
  dataTestId = "checkbox",
}: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // При отсутствии свойства `onChange` чекбокс является презентационным, то есть просто отображает переданное свойство `checked`.
  // Термин презентационный не совсем корректный с точки зрения его фактического определения, мы назвали его так потому что не придумали ничего лучше.
  // Презентационный чекбокс не будет содержать input c его нативным поведением как элемента формы (focus по клику, tab навигация).
  // В частности это логика добавлена для корректной работы с компонентом Select в `multiple` режиме, когда рядом с опциями отображается Checkbox, у которого кроме свойства `checked` ничего не указано (presentational mode).
  // В таком кейсе наличие input в Checkbox вызывает проблемы связанные с нативным поведением инпута указанным ранее (focus по клику, tab навигация). Поэтому когда свойство `onChange` не определено, мы просто не добавляем инпут чекбокса в разметку чтобы избежать таких сценариев в принципе.
  const isPresentationalMode = !onChange;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (inputRef.current) {
      onChange?.(event, {
        value: inputRef.current.value,
        checked: inputRef.current.checked,
      });
    }
  };

  const handleMouseDownOnLabel = (
    event: React.MouseEvent<HTMLLabelElement>
  ) => {
    if (isPresentationalMode) {
      // В презентационном режиме работы предотвращаем передачу фокуса на label
      event.preventDefault();
    }
  };

  const labelContent = children || label;

  return (
    <label
      htmlFor={id}
      className={cn(className, styles.checkbox, {
        [styles.checked]: checked || indeterminate,
        [styles.disabled]: disabled,
      })}
      onMouseDown={handleMouseDownOnLabel}
    >
      <span
        className={cn(styles.checkmark, styles[size])}
        data-test-id={dataTestId}
      >
        {indeterminate && (
          <MinusIcon
            className={styles.icon}
            size={size}
            dataTestId={`${dataTestId}-minus-icon`}
          />
        )}
        {checked && !indeterminate && (
          <CheckmarkIcon
            className={styles.icon}
            size={size}
            dataTestId={`${dataTestId}-checkmark-icon`}
          />
        )}

        {!isPresentationalMode && (
          <input
            id={id}
            name={name}
            value={value}
            ref={inputRef}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            className={styles.input}
            data-test-id={`${dataTestId}-input`}
          />
        )}
      </span>

      {labelContent && (
        <Text
          className={cn(styles.label, styles[size])}
          dataTestId={`${dataTestId}-label`}
          size={size}
        >
          {labelContent}
        </Text>
      )}
    </label>
  );
};
